import { Button, Chip, Typography, makeStyles } from '@material-ui/core'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import AIIcon from 'resources/AIIcon'
import React from 'react'
import { CONTRADICTION_LABEL, CROSS_REFERENCES_LABEL, OMISSION_LABEL, SPEC_INT_GRADIENT } from './specIntelligenceConstants'

const pillBase = {
    height: pxToRem(22),
    fontSize: pxToRem(11),
    '& .MuiChip-label': { padding: `0 ${pxToRem(8)}` },
} as const

const useStyles = makeStyles(() => ({
    footer: {
        borderTop: '1px solid #E0E0E0',
        padding: `${pxToRem(10)} ${pxToRem(12)}`,
        backgroundColor: '#FAFAFA',
        flexShrink: 0,
    },
    willCheckRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap' as const,
        gap: pxToRem(8),
        marginBottom: pxToRem(10),
    },
    willCheckLabel: {
        fontSize: pxToRem(10),
        fontWeight: 600,
        color: '#6D6D6D',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        flexShrink: 0,
    },
    pills: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: pxToRem(4),
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    pillCross: {
        ...pillBase,
        backgroundColor: '#FFF3E6',
        color: '#A84F15',
    },
    pillOmission: {
        ...pillBase,
        backgroundColor: '#FFCDD2',
        color: '#B71C1C',
    },
    pillContradiction: {
        ...pillBase,
        backgroundColor: '#EDE7F6',
        color: '#4527A0',
    },
    runButton: {
        width: '100%',
        textTransform: 'none' as const,
        fontWeight: 500,
        fontSize: pxToRem(13),
        '&.Mui-disabled': {
            backgroundColor: '#E0E0E0',
            color: '#9E9E9E',
        },
        '&:not(.Mui-disabled)': {
            background: SPEC_INT_GRADIENT,
            color: '#FFFFFF',
        },
    },
    runButtonIcon: {
        width: pxToRem(16),
        height: pxToRem(16),
        marginRight: pxToRem(6),
    },
}))

/** Props for {@link SpecIntelligenceAnalysisFooter}. */
export interface SpecIntelligenceAnalysisFooterProps {
    /** Number of currently selected sections; drives button label and disabled state. */
    selectedCount: number
    /** Called when the user clicks Run Analysis. */
    onRunAnalysis: () => void
}

/**
 * Footer for the Spec Intelligence drawer — displays the "will check for" category
 * pills and the Run Analysis button.
 */
const SpecIntelligenceAnalysisFooter = ({ selectedCount, onRunAnalysis }: SpecIntelligenceAnalysisFooterProps) => {
    const classes = useStyles()
    return (
        <div className={classes.footer}>
            <div className={classes.willCheckRow}>
                <Typography
                    id="spec-intel-will-check-label"
                    component="h3"
                    className={classes.willCheckLabel}
                >
                    Will check for
                </Typography>
                <ul
                    className={classes.pills}
                    aria-labelledby="spec-intel-will-check-label"
                >
                    <li>
                        <Chip label={CROSS_REFERENCES_LABEL} size="small" className={classes.pillCross} />
                    </li>
                    <li>
                        <Chip label={OMISSION_LABEL} size="small" className={classes.pillOmission} />
                    </li>
                    <li>
                        <Chip label={CONTRADICTION_LABEL} size="small" className={classes.pillContradiction} />
                    </li>
                </ul>
            </div>
            <Button
                disabled={selectedCount === 0}
                fullWidth
                onClick={onRunAnalysis}
                className={classes.runButton}
                startIcon={
                    <AIIcon className={classes.runButtonIcon} />
                }
            >
                Run Analysis ({selectedCount} section{selectedCount !== 1 ? 's' : ''})
            </Button>
        </div>
    )
}

export default SpecIntelligenceAnalysisFooter
